import { capitalizeString } from '@order-management/format';
import { UserPermissions } from '@order-management/roles';
import {
  DeferredResponse,
  HomeSections,
  InputType,
  MyTeamTableFilterDataType,
  PaginatedAPIResponse,
  PaginatedContentConfig,
  SortDirection,
  TeamCriteria,
  User,
  UserStatus,
} from '@order-management/types';
import { Suspense, useCallback, useMemo, useState } from 'react';
import { Columns, Element, Icon } from 'react-bulma-components';
import { FaPlus } from 'react-icons/fa6';
import {
  LoaderFunction,
  defer,
  useAsyncValue,
  useLoaderData,
  useNavigate,
  useNavigation,
  useSearchParams,
} from 'react-router-dom';
import CheckPermissions from '../../shared/check-permissions/check-permissions';
import Conditional from '../../shared/conditional/conditional';
import ErrorHandlingAwait from '../../shared/error-handling-await/error-handling-await';
import FilterBar, { FiltersData } from '../../shared/filter-bar/filter-bar';
import HomeSectionsHeaderTabs from '../../shared/home-sections-header-tabs/home-sections-header-tabs';
import useHttpRequest from '../../shared/hooks/http-request';
import {
  createSearchParams,
  getSearchParamValue,
  getSearchParamValues,
} from '../../shared/url';
import BoxLayout from '../../ui/box-layout/box-layout';
import Button from '../../ui/button/button';
import InboxTable from '../../ui/inbox-table/inbox-table';
import PageSpinner from '../../ui/page-spinner/page-spinner';
import { OnTableChangeData } from '../../ui/table/types';
import MyTeamTable from '../my-team-table/my-team-table';
import { fetchMyTeam } from '../users-service';
import { PermissionsGroupBySalesOrder } from '../users-table/users-table';

const initialCriteriaData = (searchParams?: URLSearchParams): TeamCriteria => ({
  itemsPerPage: parseInt(
    getSearchParamValue(searchParams, 'itemsPerPage') ?? '50'
  ),
  pageNumber: parseInt(getSearchParamValue(searchParams, 'pageNumber') ?? '1'),
  search: getSearchParamValue(searchParams, 'search') ?? '',
  sortBy: getSearchParamValue(searchParams, 'sortBy') ?? '',
  sortDirection:
    getSearchParamValue<SortDirection>(searchParams, 'sortDirection') ??
    SortDirection.ASC,
  email: getSearchParamValues(searchParams, 'email'),
  status: getSearchParamValues<UserStatus>(searchParams, 'status'),
  salesOrder: getSearchParamValues(searchParams, 'salesOrder'),
});

export const loader: LoaderFunction = async ({ request }) => {
  const searchParams = createSearchParams(request);
  const criteria = initialCriteriaData(searchParams);

  const response = fetchMyTeam(criteria);

  return defer({ response });
};

export function MyTeamPageContent() {
  const response = useAsyncValue() as PaginatedAPIResponse<User>;

  const [searchParams] = useSearchParams();
  const navigation = useNavigation();
  const navigate = useNavigate();
  const { executeHttpRequest: executeFetchUsers, isLoading: isFetchingUsers } =
    useHttpRequest();
  const { executeHttpRequest: executeFetchMoreUsers } = useHttpRequest();

  const [criteria, setCriteria] = useState<TeamCriteria>(
    initialCriteriaData(searchParams)
  );
  const [users, setUsers] = useState<User[]>(response.data);
  const [hasMore, setHasMore] = useState<boolean>(!!response.hasMore);

  const tableConfig: PaginatedContentConfig = useMemo(
    () => ({ ...criteria, pages: response.pages }),
    [criteria, response.pages]
  );

  const filtersData = useMemo(() => {
    return {
      email: {
        title: 'User',
        appliedCount: criteria.email?.length,
        value: criteria.email,
        input: {
          type: InputType.FILTER_CRITERIA,
          filterValueKey: 'email',
          valueKey: 'email',
          placeholder: 'Search by User',
        },
      },
      salesOrder: {
        title: 'Sales order',
        appliedCount: criteria.salesOrder?.length,
        value: criteria.salesOrder,
        input: {
          type: InputType.FILTER_CRITERIA,
          filterValueKey: 'salesOrder',
          valueKey: 'salesOrder',
          placeholder: 'Search by SO',
        },
      },
      status: {
        title: 'Status',
        appliedCount: criteria.status?.length,
        value: criteria.status,
        input: {
          type: InputType.SELECT,
          valueKey: 'status',
          options: [UserStatus.ACTIVE, UserStatus.PENDING_FIRST_LOGIN].map(
            (status) => ({
              label: capitalizeString(status),
              value: status,
            })
          ),
        },
      },
    };
  }, [
    criteria.email,
    criteria.salesOrder,
    criteria.status,
  ]) as FiltersData<TeamCriteria>;

  const emptyContentText = useMemo(() => {
    const hasAppliedFilters = Object.entries(filtersData).some(
      ([_, filter]) => filter.appliedCount
    );
    if (hasAppliedFilters) {
      return 'There are no users that match the selected criteria';
    }
    return 'There are no users at the moment';
  }, [filtersData]);

  const fetchUsers = useCallback(
    async (myTeamCriteria: TeamCriteria) =>
      executeFetchUsers({
        asyncFunction: async () => {
          const fetchedUsers = await fetchMyTeam(myTeamCriteria);
          setUsers(fetchedUsers.data);
          setHasMore(fetchedUsers.hasMore!);
        },
      }),
    [executeFetchUsers]
  );

  const fetchMoreUsers = useCallback(
    () =>
      executeFetchMoreUsers({
        asyncFunction: async () => {
          const nextPage = criteria.pageNumber + 1;
          const updatedCriteria = {
            ...criteria,
            pageNumber: nextPage,
          };
          const newUsers = await fetchMyTeam(updatedCriteria);
          setUsers((prevUsers) => ({
            ...prevUsers,
            data: [...prevUsers, ...newUsers.data],
          }));
          setCriteria(updatedCriteria);
          setHasMore(newUsers.hasMore!);
        },
      }),
    [criteria, executeFetchMoreUsers]
  );

  const handleOnTableChange = useCallback(
    (value: OnTableChangeData<User, PermissionsGroupBySalesOrder>): void => {
      const newCriteria = {
        ...criteria,
        ...value.config,
        sortBy: value.column?.key || criteria.sortBy,
      };

      setCriteria(newCriteria);
      fetchUsers(newCriteria);
    },
    [criteria, fetchUsers]
  );

  const handleInviteUser = useCallback(() => {
    navigate('/users/my-team/invite');
  }, [navigate]);

  const handleTableFilterSubmit = useCallback(
    (tableFilterValue: MyTeamTableFilterDataType) => {
      const newCriteria = { ...criteria, ...tableFilterValue, pageNumber: 1 };
      setCriteria(newCriteria);
      fetchUsers(newCriteria);
    },
    [criteria, fetchUsers]
  );

  const onRemoveFilters = useCallback(
    (filterDataKeys: (keyof TeamCriteria)[]) => {
      const newCriteria = { ...criteria };
      for (const filterDataKey of filterDataKeys) {
        const initialFilterValue = initialCriteriaData()[filterDataKey];
        if (initialFilterValue === undefined) {
          delete newCriteria[filterDataKey];
        } else {
          newCriteria[filterDataKey] = initialFilterValue as string &
            string[] &
            boolean;
        }
      }
      setCriteria(newCriteria);
      fetchUsers(newCriteria);
    },
    [criteria, fetchUsers]
  );

  const navigateToEditUserPermissions = useCallback(
    (user: User) => {
      navigate(`/users/my-team/${user.id}/edit`);
    },
    [navigate]
  );

  const TeamFilters = useCallback(
    () => (
      <Element display="flex" className="w-full" alignItems="center">
        <FilterBar
          initialData={initialCriteriaData()}
          data={criteria}
          filtersData={filtersData}
          onFilterSubmit={handleTableFilterSubmit}
          onRemoveFilter={onRemoveFilters}
        />
      </Element>
    ),
    [criteria, filtersData, handleTableFilterSubmit, onRemoveFilters]
  );

  return (
    <BoxLayout
      className="is-min-height-80 mx-10 mb-4"
      headerClassName="is-radiusless"
      header={
        <Columns marginless>
          <Columns.Column className="pl-4">
            <h1 className="is-size-3 has-text-weight-light">My Team</h1>
            <h2 className="is-size-5 has-text-weight-light">
              All Users related to your Sales Orders. You can optionally add
              Users to any Sales Order where you have manager permissions.
            </h2>
          </Columns.Column>

          <Columns.Column
            display="flex"
            justifyContent="flex-end"
            className="gap-3"
          >
            <CheckPermissions permissions={UserPermissions.USERS_CREATE}>
              <CheckPermissions.Render>
                <Button color="primary-dark" onClick={handleInviteUser}>
                  <Icon>
                    <FaPlus />
                  </Icon>
                  <span>Add a team member</span>
                </Button>
              </CheckPermissions.Render>
            </CheckPermissions>
          </Columns.Column>
        </Columns>
      }
    >
      <Element>
        <Conditional
          condition={navigation.state === 'loading' || isFetchingUsers}
        >
          <Conditional.True>
            <PageSpinner />
          </Conditional.True>
          <Conditional.False>
            <TeamFilters />
            <InboxTable
              table={
                <MyTeamTable
                  dataSource={users}
                  config={tableConfig}
                  fetchMore={fetchMoreUsers}
                  hasMore={!!hasMore}
                  empty={
                    <Element
                      display="flex"
                      justifyContent="center"
                      className="my-2"
                    >
                      <span className="has-text-weight-light">
                        {emptyContentText}
                      </span>
                    </Element>
                  }
                  onChange={handleOnTableChange}
                  handleEditUserClick={navigateToEditUserPermissions}
                />
              }
              isTableEmpty={users.length === 0}
              config={tableConfig}
              enableSearch={false}
            />
          </Conditional.False>
        </Conditional>
      </Element>
    </BoxLayout>
  );
}

export function MyTeamPage() {
  const { response } = useLoaderData() as DeferredResponse<
    PaginatedAPIResponse<User>
  >;

  const Skeleton = () => (
    <BoxLayout
      className="is-min-height-80 mx-10 mb-4"
      headerClassName="is-radiusless"
      header={
        <Columns marginless>
          <Columns.Column className="pl-4">
            <h1 className="is-size-3 has-text-weight-light">My Team</h1>
            <h2 className="is-size-5 has-text-weight-light">
              All Users related to your Sales Orders. You can optionally add
              Users to any Sales Order where you have manager permissions.
            </h2>
          </Columns.Column>
        </Columns>
      }
    >
      <PageSpinner />
    </BoxLayout>
  );

  return (
    <>
      <HomeSectionsHeaderTabs activeTab={HomeSections.TEAM} />

      <Suspense fallback={<Skeleton />}>
        <ErrorHandlingAwait resolve={response}>
          <MyTeamPageContent />
        </ErrorHandlingAwait>
      </Suspense>
    </>
  );
}

export default MyTeamPage;
